import type { ComponentProps } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils.js';

/**
 * Кнопка shadcn/ui, приведённая к продукту:
 *   - варианты `dark:` удалены — тёмной темы в MathMind нет (§4 CLAUDE.md), это был бы мёртвый код;
 *   - директива "use client" удалена — она из Next.js, здесь Vite SPA;
 *   - добавлен вариант `pay` под токен --mm-pay: в проде кнопка оплаты «Վճարել» имеет
 *     собственный зелёный (#52BE80, styleIndex5.css:501), отличный от основного цвета бренда.
 *
 * Цвета не прописаны литералами: variant `default` берёт --primary, который в
 * styles/tokens.css отображён на --mm-ink. Перекраска бренда — правка одного файла токенов.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        /**
         * Надпись тёмная, а не белая. Замерено: белый на #52BE80 даёт контраст 2.32:1 и
         * проваливает WCAG AA даже для крупного текста; --mm-ink на том же фоне — 5.66:1.
         * Меняется ИМЕННО цвет надписи, а не фон: боевой зелёный сохраняется точно.
         */
        pay: 'bg-mm-pay text-mm-ink hover:bg-mm-pay/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline: 'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        /**
         * Кнопки прода. Bootstrap 5.3 один в один: `.btn` даёт рамку 1px и вес 400,
         * `.btn-secondary` — рамку #6c757d, которую прод НЕ перекрашивал, хотя фон и текст
         * задал инлайном (замерено на mathmind.am: border 1px solid rgb(108,117,125)).
         * Без неё светлая кнопка входа выглядит плоской нашлёпкой, а не кнопкой.
         */
        bsLight: 'border border-[#6c757d] bg-mm-surface text-mm-ink hover:brightness-95',
        /** Тёмная кнопка призыва и поиска: инлайновый `background:#2c2f44` прода. */
        bsDark: 'border border-transparent bg-mm-ink text-white hover:brightness-110',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        /**
         * `.btn` Bootstrap: padding 6px 12px, 16px, радиус 6px, вес 400.
         *
         * ⚠️ `min-h-[38px]` обязателен. В проде высота набирается строкой текста
         * (line-height 1.5 → 24px) плюс отступы и рамка = ровно 38px, и такой же остаётся
         * кнопка, внутри которой только иконка. Здесь контент лежит во flex-строке, иконка
         * даёт 16px вместо 24, и кнопка аккаунта выходила на 8px ниже прода — вместе с ней
         * съезжала вся шапка.
         */
        bs: 'min-h-[38px] rounded-[0.375rem] px-3 py-[0.375rem] text-base leading-normal font-normal',
        /** `.btn-lg`: 8px 16px, 20px, радиус 8px, высота 48px по той же арифметике. */
        bsLg: 'min-h-[48px] rounded-lg px-4 py-2 text-xl leading-normal font-normal',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
